/**
 * Selection Feature
 * Manages file/folder selection state for the file tree
 */

import { useState, useCallback, useMemo } from "react";
import { Datum, Filetree } from "../datastructure";
import { isSelectionBlockedFile } from "../rendering";

/** Check if a datum is a file a picker refuses: one still being processed,
 *  or a locked one. A failed file is selectable (#13887). */
const isDatumSelectionBlocked = (datum: Datum): boolean =>
  datum.type === "file" && isSelectionBlockedFile(datum.payload);

/** A single selection action (select or deselect a datum) */
interface SelectionAction {
  type: "select" | "deselect";
  datumId: string;
}

/** A folder selection with drive ID and path */
export interface FolderSelection {
  driveId: string | null;
  path: string[];
}

/** Computed selection state */
export interface SelectionState {
  /** Array of selected file IDs */
  fileIds: string[];
  /** Array of selected files with metadata */
  files: { id: string; name: string; type: string }[];
  /** Array of selected folder selections (with drive ID and path) */
  folderSelections: FolderSelection[];
  /** Whether any items are selected */
  hasSelection: boolean;
  /** Number of selected files */
  fileCount: number;
  /** Number of selected folders */
  folderCount: number;
  /** Total number of selected items */
  totalCount: number;
}

export interface UseSelectionOptions {
  /** The filetree instance */
  filetree: Filetree;
  /** Whether in across-drives mode (affects path stripping) */
  isAcrossDrivesMode: boolean;
  /**
   * Whether still-processing (greyed-out) files may be selected. The Documents
   * page opts in so users can bulk move/delete/open them (#10770); the query /
   * report "Select Documents" modals leave it false so unprocessed files stay
   * unselectable (#9880). Defaults to false.
   */
  allowProcessingFileSelection?: boolean;
}

/** Minimal metadata for a pending file */
export interface PendingFileMetadata {
  name: string;
  type: string;
}

export interface UseSelectionReturn {
  /** Computed selection state */
  selection: SelectionState;
  /** Check if an item is selected (by datum ID) */
  isItemSelected: (itemId: string) => boolean;
  /** Check if an item is in a selected context (itself or any ancestor is selected) */
  isItemInSelectedContext: (itemId: string) => boolean;
  /** Toggle selection for an item */
  toggleItemSelection: (itemId: string) => void;
  /** Clear all selections */
  clearSelection: () => void;
  /** Select files by their file IDs (replaces current selection) */
  selectFiles: (
    fileIds: string[],
    metadata?: Map<string, PendingFileMetadata>
  ) => void;
  /** Select folders by their paths (merges with file selections) */
  selectFolders: (folderPaths: string[][]) => void;
  /** Set both file and folder selections at once */
  setSelection: (
    fileIds: string[],
    folderPaths?: string[][],
    metadata?: Map<string, PendingFileMetadata>
  ) => void;
  /** Reset selection state (called when filetree changes) */
  resetSelection: () => void;
  /** Update metadata for existing pending files without changing selection state */
  updatePendingMetadata: (updates: Map<string, PendingFileMetadata>) => void;
}

/**
 * Hook for managing file tree selection state
 */
export function useSelection({
  filetree,
  isAcrossDrivesMode,
  allowProcessingFileSelection = false,
}: UseSelectionOptions): UseSelectionReturn {
  const [selectionActions, setSelectionActions] = useState<SelectionAction[]>(
    []
  );

  // Track files that are selected but not yet in the tree (e.g., from search results)
  // Map from fileId -> metadata (name, type)
  const [pendingFiles, setPendingFiles] = useState<
    Map<string, PendingFileMetadata>
  >(new Map());

  // Track folder paths that are selected but not yet in the tree (e.g., from initial selection before tree loads)
  const [pendingFolderPaths, setPendingFolderPaths] = useState<Set<string>>(
    new Set()
  );

  // Walk ancestors to find most recent selection action, also check pendingFiles/pendingFolderPaths
  const isItemSelected = useCallback(
    (itemId: string): boolean => {
      const datum = filetree.lookup.get(itemId);

      // Check if this file is in pendingFiles (selected from search before datum existed)
      if (datum?.type === "file" && datum.payload?.type === "file") {
        const fileId = datum.payload.fileId;
        if (fileId && pendingFiles.has(fileId)) {
          return true;
        }
      }

      // Check if this folder is in pendingFolderPaths (selected before tree loaded)
      if (datum?.type === "folder") {
        const pathKey = JSON.stringify(datum.path);
        if (pendingFolderPaths.has(pathKey)) {
          return true;
        }
      }

      // Check selection actions (walk ancestors for folder inheritance)
      let current = datum;
      while (current) {
        // Check if an ancestor folder is in pendingFolderPaths
        if (current.type === "folder" && pendingFolderPaths.size > 0) {
          const pathKey = JSON.stringify(current.path);
          if (pendingFolderPaths.has(pathKey)) {
            return true;
          }
        }
        for (let i = selectionActions.length - 1; i >= 0; i--) {
          if (selectionActions[i].datumId === current.id) {
            return selectionActions[i].type === "select";
          }
        }
        current = current.parent;
      }
      return false;
    },
    [filetree, selectionActions, pendingFiles, pendingFolderPaths]
  );

  // Check if an item is in a selected context: itself is selected, or any ancestor is selected.
  // Unlike isItemSelected, this returns true even for deselected children of selected folders.
  const isItemInSelectedContext = useCallback(
    (itemId: string): boolean => {
      const datum = filetree.lookup.get(itemId);
      if (!datum) return false;
      if (
        datum.type === "file" &&
        !allowProcessingFileSelection &&
        isDatumSelectionBlocked(datum)
      )
        return false;

      let current: Datum | undefined = datum;
      while (current) {
        if (isItemSelected(current.id)) return true;
        current = current.parent;
      }
      return false;
    },
    [filetree, isItemSelected, allowProcessingFileSelection]
  );

  const toggleItemSelection = useCallback(
    (itemId: string) => {
      const datum = filetree.lookup.get(itemId);

      // If this file is in pendingFiles, remove it so both states get cleared
      if (datum?.type === "file" && datum.payload?.type === "file") {
        const fileId = datum.payload.fileId;
        if (fileId && pendingFiles.has(fileId)) {
          setPendingFiles((prev) => {
            const newMap = new Map(prev);
            newMap.delete(fileId);
            return newMap;
          });
        }
      }

      // If this folder is in pendingFolderPaths, remove it so both states get cleared
      if (datum?.type === "folder") {
        const pathKey = JSON.stringify(datum.path);
        if (pendingFolderPaths.has(pathKey)) {
          setPendingFolderPaths((prev) => {
            const newSet = new Set(prev);
            newSet.delete(pathKey);
            return newSet;
          });
        }
      }

      // Normal toggle via selection actions
      const type = isItemSelected(itemId)
        ? ("deselect" as const)
        : ("select" as const);
      const actions: SelectionAction[] = [{ type, datumId: itemId }];

      // When toggling a folder, also toggle all loaded descendants
      if (datum?.type === "folder") {
        const collectDescendants = (parent: Datum) => {
          for (const child of parent.children ?? []) {
            if (!allowProcessingFileSelection && isDatumSelectionBlocked(child))
              continue;
            actions.push({ type, datumId: child.id });
            if (child.type === "folder") {
              collectDescendants(child);
            }
          }
        };
        collectDescendants(datum);
      }

      setSelectionActions((prev) => [...prev, ...actions]);
    },
    [
      isItemSelected,
      filetree,
      pendingFiles,
      pendingFolderPaths,
      allowProcessingFileSelection,
    ]
  );

  const clearSelection = useCallback(() => {
    setSelectionActions([]);
    setPendingFiles(new Map());
    setPendingFolderPaths(new Set());
  }, []);

  const resetSelection = useCallback(() => {
    setSelectionActions([]);
    setPendingFiles(new Map());
    setPendingFolderPaths(new Set());
  }, []);

  /**
   * Programmatically select files by their file IDs.
   * This adds to the current selection.
   * Files that aren't loaded in the tree yet are stored in pendingFiles.
   * Optional metadata map provides name/type for pending files.
   */
  const selectFiles = useCallback(
    (fileIds: string[], metadata?: Map<string, PendingFileMetadata>) => {
      const newActions: SelectionAction[] = [];
      const foundFileIds = new Set<string>();

      // Find datums by file ID and add select actions
      for (const datum of filetree.datums) {
        if (
          datum.type === "file" &&
          datum.payload?.type === "file" &&
          datum.payload.fileId &&
          fileIds.includes(datum.payload.fileId)
        ) {
          newActions.push({ type: "select", datumId: datum.id });
          foundFileIds.add(datum.payload.fileId);
        }
      }

      // Files not found in tree are added to pendingFiles
      const notFoundFileIds = fileIds.filter((id) => !foundFileIds.has(id));

      setSelectionActions((prev) => [...prev, ...newActions]);
      if (notFoundFileIds.length > 0) {
        setPendingFiles((prev) => {
          const newMap = new Map(prev);
          for (const id of notFoundFileIds) {
            // Keep a name we already knew rather than blanking it (#11242).
            // A file in a drive this tree hasn't loaded is "not found" here,
            // and overwriting its metadata with an empty name is what renders
            // it as a nameless chip in the chat composer.
            newMap.set(
              id,
              metadata?.get(id) || prev.get(id) || { name: "", type: "" }
            );
          }
          return newMap;
        });
      }
    },
    [filetree]
  );

  /**
   * Programmatically select folders by their paths.
   * Path format must match what the tree uses (string arrays).
   */
  const selectFolders = useCallback(
    (folderPaths: string[][]) => {
      const newActions: SelectionAction[] = [];
      const pathStrings = new Set(folderPaths.map((p) => JSON.stringify(p)));

      for (const datum of filetree.datums) {
        if (
          datum.type === "folder" &&
          pathStrings.has(JSON.stringify(datum.path))
        ) {
          newActions.push({ type: "select", datumId: datum.id });
        }
      }

      // Merge with existing file selections
      setSelectionActions((prev) => {
        const existingFileSelections = prev.filter((a) => {
          const d = filetree.lookup.get(a.datumId);
          return d?.type === "file";
        });
        return [...existingFileSelections, ...newActions];
      });
    },
    [filetree]
  );

  /**
   * Set both file and folder selections at once.
   * Clears existing selection and applies the new one.
   * Files not found in the tree are stored in pendingFiles.
   * Folders not found in the tree are stored in pendingFolderPaths.
   * Preserves existing metadata for re-selected pending files.
   */
  const setSelection = useCallback(
    (
      fileIds: string[],
      folderPaths: string[][] = [],
      metadata?: Map<string, PendingFileMetadata>
    ) => {
      const newActions: SelectionAction[] = [];
      const pathStrings = new Set(folderPaths.map((p) => JSON.stringify(p)));
      const foundFileIds = new Set<string>();
      const foundFolderPaths = new Set<string>();

      for (const datum of filetree.datums) {
        if (datum.type === "file") {
          const fileId =
            datum.payload?.type === "file" ? datum.payload.fileId : null;
          if (fileId && fileIds.includes(fileId)) {
            newActions.push({ type: "select", datumId: datum.id });
            foundFileIds.add(fileId);
          }
        } else if (datum.type === "folder") {
          const pathKey = JSON.stringify(datum.path);
          if (pathStrings.has(pathKey)) {
            newActions.push({ type: "select", datumId: datum.id });
            foundFolderPaths.add(pathKey);
          }
        }
      }

      // Files not found in tree are stored in pendingFiles
      const notFoundFileIds = fileIds.filter((id) => !foundFileIds.has(id));

      // Folders not found in tree are stored in pendingFolderPaths
      const notFoundFolderPaths = folderPaths
        .map((p) => JSON.stringify(p))
        .filter((p) => !foundFolderPaths.has(p));

      setSelectionActions(newActions);
      setPendingFiles((prev) => {
        const newMap = new Map<string, PendingFileMetadata>();
        for (const id of notFoundFileIds) {
          // Priority: explicit metadata > existing metadata > empty
          newMap.set(
            id,
            metadata?.get(id) || prev.get(id) || { name: "", type: "" }
          );
        }
        return newMap;
      });
      setPendingFolderPaths(new Set(notFoundFolderPaths));
    },
    [filetree]
  );

  /**
   * Compute the actual selected file IDs and folder paths from the selection actions.
   * This extracts concrete selection data that consumers can use.
   * Also includes pendingFiles for files selected from search but not in tree.
   */
  const selection = useMemo(() => {
    const fileIds: string[] = [];
    const files: { id: string; name: string; type: string }[] = [];
    const folderSelections: FolderSelection[] = [];
    const seenFileIds = new Set<string>();

    // Build a map of datumId -> latest action for efficient lookup
    const latestAction = new Map<string, "select" | "deselect">();
    for (const action of selectionActions) {
      latestAction.set(action.datumId, action.type);
    }

    // Build set of folders that have descendant deselects — these folders
    // should not be included as folder selections because restoring them
    // would re-select the individually deselected children.
    const foldersWithDescendantDeselects = new Set<string>();
    for (const [datumId, actionType] of latestAction) {
      if (actionType !== "deselect") continue;
      const datum = filetree.lookup.get(datumId);
      if (!datum) continue;
      let ancestor = datum.parent;
      while (ancestor && ancestor.path.length > 0) {
        const ancestorAction = latestAction.get(ancestor.id);
        if (ancestorAction === "select") {
          foldersWithDescendantDeselects.add(ancestor.id);
        }
        ancestor = ancestor.parent;
      }
    }

    // Helper to check if a datum is selected (same logic as isItemSelected but using the map)
    const checkSelected = (datum: Datum): boolean => {
      let current: Datum | undefined = datum;
      while (current) {
        const action = latestAction.get(current.id);
        if (action !== undefined) {
          return action === "select";
        }
        current = current.parent;
      }
      return false;
    };

    // Walk all datums and collect selected ones
    for (const datum of filetree.datums) {
      if (datum.path.length === 0) continue; // Skip root

      if (!checkSelected(datum)) continue;

      if (datum.type === "file" && datum.payload?.type === "file") {
        const fileId = datum.payload.fileId;
        if (fileId) {
          fileIds.push(fileId);
          seenFileIds.add(fileId);
          files.push({
            id: fileId,
            name: datum.payload.fileName || datum.name || "",
            type: datum.payload.fileType || "",
          });
        }
      } else if (
        datum.type === "folder" &&
        !foldersWithDescendantDeselects.has(datum.id)
      ) {
        // In across-drives mode WITH drives enabled, first path element is the drive ID
        // Extract driveId and actual folder path separately
        if (filetree.shouldStripDrivePrefix && datum.path.length > 0) {
          const driveId = datum.path[0];
          const path = datum.path.slice(1);
          folderSelections.push({ driveId, path });
        } else {
          // Not in across-drives mode or drives disabled, use payload driveId if available
          const driveId = datum.payload?.driveId || null;
          folderSelections.push({ driveId, path: datum.path });
        }
      }
    }

    // Add pending files (selected from search but not yet in tree)
    for (const [pendingId, meta] of pendingFiles) {
      if (!seenFileIds.has(pendingId)) {
        fileIds.push(pendingId);
        files.push({
          id: pendingId,
          name: meta.name,
          type: meta.type,
        });
      }
    }

    // Add pending folder paths (selected before tree loaded those folders)
    // Track what we already have to avoid duplicates
    const existingFolderKeys = new Set(
      folderSelections.map((fs) =>
        JSON.stringify(fs.driveId ? [fs.driveId, ...fs.path] : fs.path)
      )
    );
    for (const pendingPathKey of pendingFolderPaths) {
      if (!existingFolderKeys.has(pendingPathKey)) {
        const pathArray: string[] = JSON.parse(pendingPathKey);
        // In across-drives mode with drives enabled, first element is driveId
        if (filetree.shouldStripDrivePrefix && pathArray.length > 0) {
          const driveId = pathArray[0];
          const path = pathArray.slice(1);
          folderSelections.push({ driveId, path });
        } else {
          folderSelections.push({ driveId: null, path: pathArray });
        }
      }
    }

    return {
      fileIds,
      files,
      folderSelections,
      hasSelection: fileIds.length > 0 || folderSelections.length > 0,
      fileCount: fileIds.length,
      folderCount: folderSelections.length,
      totalCount: fileIds.length + folderSelections.length,
    };
  }, [
    filetree.datums,
    selectionActions,
    isAcrossDrivesMode,
    pendingFiles,
    pendingFolderPaths,
  ]);

  /**
   * Update metadata for existing pending files without changing selection state.
   * Used for lazy resolution of file names after "Show selected" is toggled.
   */
  const updatePendingMetadata = useCallback(
    (updates: Map<string, PendingFileMetadata>) => {
      setPendingFiles((prev) => {
        const newMap = new Map(prev);
        for (const [id, meta] of updates) {
          if (newMap.has(id)) {
            newMap.set(id, meta);
          }
        }
        return newMap;
      });
    },
    []
  );

  return {
    selection,
    isItemSelected,
    isItemInSelectedContext,
    toggleItemSelection,
    clearSelection,
    selectFiles,
    selectFolders,
    setSelection,
    resetSelection,
    updatePendingMetadata,
  };
}
